import React, { useCallback, useContext } from 'react';

import { Box } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import { UserPreview } from '@vyce/core/src/views/preview/user/UserPreview';
import {
  getHealthAndSafetyPDFByUserIdRequest,
  getPassportRequest as getTeamsPassportRequest,
  getRtwRequest as getTeamsRtwRequest,
  getTeamMemberPreviewRequest,
  createCustomQualificationRequest,
  createQualificationRequest,
  uploadCustomQualificationImageRequest,
  updateQualificationRequest,
  deleteCustomQualificationImageRequest,
} from '@vyce/core/src/api/teams';
import { DeviceContext } from '@vyce/core/src/contexts';
import { useEmployeeProfileData } from '@vyce/core/src/modules/userPreviewModule/hooks/useEmployeeProfileData';
import { QualificationDialog } from '@vyce/core/src/modules/qualificationDialog';

import { useTypedSelector } from '../../../hooks';
import { deleteCustomQualificationRequest, getCheckersRequest } from '@vyce/core/src/api/checkers';
import { getTagListRequest } from '@vyce/core/src/api/handbook';
import { ChatUser, CustomCertificate, User } from '@vyce/core/src/types';
import { GetUserData } from '@vyce/core/src/api/types';
import { UserPreviewProvider } from '@vyce/core/src/contexts/userPreviewProvider';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

interface Props {
  /** Defaults to the teams route; the main dashboard injects the gateway `/core` request. */
  getUserRequest?: (params: GetUserData) => Promise<AxiosResponse>;
  /** Default to the teams routes; the main dashboard swaps in pay's for payroll-only roles. */
  getPassportRequest?: (params: GetUserData) => Promise<AxiosResponse>;
  getRtwRequest?: (params: GetUserData) => Promise<AxiosResponse>;
}

export const TeamMemberPreview: React.FC<Props> = ({
  getUserRequest = getTeamMemberPreviewRequest,
  getPassportRequest = getTeamsPassportRequest,
  getRtwRequest = getTeamsRtwRequest,
}) => {
  const { selectedCompany } = useTypedSelector(state => state.helper);
  const history = useHistory();
  const {
    userPreviewData,
    rtw,
    passport,
    hasPayrollPermissions,
    hasPermissionForCreateQualification,
    selectedCompanyId,
    userId,
    isShowAgencyData,
    contextOptions,
    getUserPreviewData,
  } = useEmployeeProfileData({
    getEmployeePreviewRequest: getUserRequest,
    getPassportRequest,
    getRtwRequest,
    selectedCompany,
  });
  const { isMobile } = useContext(DeviceContext);
  const { first_name, last_name, email, phone, photo, uuid } = useTypedSelector(state => state.user);
  const { createPrivateChat } = useChatRequests();
  const chatCreator: ChatUser = {
    uid: uuid,
    name: `${first_name} ${last_name}`,
    email,
    phone,
    avatarUrl: photo?.url,
  };

  const getPayrollDetailsLink = useCallback(
    (tab: string): string => {
      return hasPayrollPermissions ? `${history.location.pathname}/${tab}` : '';
    },
    [hasPayrollPermissions]
  );

  const goToChat = async (userData: User) => {
    if (!chatCreator || !selectedCompany?.uuid) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: userData.uuid,
        name: `${userData.first_name} ${userData.last_name}`,
        email: userData.email,
        phone: userData.phone,
        avatarUrl: userData.photo?.url,
      },
      companyId: selectedCompany?.uuid,
    });
  };

  return (
    <Box padding={isMobile ? 1 : 5}>
      <UserPreviewProvider value={contextOptions}>
        {userPreviewData && (
          <UserPreview
            rtw={rtw}
            createPrivateChat={() => goToChat(userPreviewData.user)}
            passport={passport}
            userPreviewData={userPreviewData}
            timesheetsLink={getPayrollDetailsLink('timesheets')}
            paymentsLink={getPayrollDetailsLink('payments')}
            agreementsLink={getPayrollDetailsLink('agreements')}
            companyId={selectedCompany?.uuid ?? ''}
            userId={userId}
            deleteCustomQualificationRequest={deleteCustomQualificationRequest}
            showHealthAndSafetyPDFRequest={getHealthAndSafetyPDFByUserIdRequest}
            withAddQualificationModal={true}
            hasPermissionForCreateQualification={hasPermissionForCreateQualification}
            isShowAgencyData={isShowAgencyData}
            renderQualificationModal={({
              isOpen,
              selectedQualification,
              onClose,
            }: {
              isOpen: boolean;
              selectedQualification: CustomCertificate;
              onClose: () => void;
            }) => (
              <QualificationDialog
                isAdmin={false}
                isOpen={isOpen}
                onClose={onClose}
                selectedQualification={selectedQualification as CustomCertificate}
                reRequestData={getUserPreviewData}
                getCheckersRequest={getCheckersRequest}
                triggerCheckerRequest={createQualificationRequest}
                createCustomQualificationRequest={createCustomQualificationRequest}
                updateCustomQualificationRequest={updateQualificationRequest}
                uploadCustomQualificationImageRequest={uploadCustomQualificationImageRequest}
                deleteCustomQualificationImageRequest={deleteCustomQualificationImageRequest}
                getTagListRequest={getTagListRequest}
                type="outside"
                userId={userId}
                selectedCompanyId={selectedCompanyId}
              />
            )}
          />
        )}
      </UserPreviewProvider>
    </Box>
  );
};
